import React, { useState } from 'react';
import { Book, Code, Key, CreditCard, BarChart3, Copy, ChevronDown, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

interface CodeExample {
  language: string;
  code: string;
}

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
}

interface ApiEndpoint {
  method: string;
  path: string;
  title: string;
  description: string;
  auth: 'jwt' | 'api-key' | 'none';
  parameters?: Parameter[];
  requestBody?: {
    type: string;
    properties: Parameter[];
  };
  responses: {
    code: number;
    description: string;
    example: any;
  }[];
  examples: CodeExample[];
}

const APIReference: React.FC = () => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    authentication: true,
    getting_started: true,
  });
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const apiEndpoints: Record<string, ApiEndpoint[]> = {
    authentication: [
      {
        method: 'POST',
        path: '/api/v1/auth/login',
        title: 'Portal Login',
        description: 'Authenticate to access the portal and manage API keys',
        auth: 'none',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'email', type: 'string', required: true, description: 'User email address', example: 'user@fenxlabs.ai' },
            { name: 'password', type: 'string', required: true, description: 'User password', example: 'password123' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'Login successful',
            example: {
              success: true,
              data: {
                token: 'jwt_token_here',
                user: {
                  uid: 'user_id',
                  email: 'user@fenxlabs.ai',
                  creditBalance: 10.0
                }
              }
            }
          },
          {
            code: 401,
            description: 'Invalid credentials',
            example: {
              success: false,
              error: {
                code: 'INVALID_CREDENTIALS',
                message: 'Invalid email or password'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@fenxlabs.ai",
    "password": "password123"
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/auth/login', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    email: 'user@fenxlabs.ai',
    password: 'password123'
  })
});

const data = await response.json();
const token = data.data.token;`
          },
          {
            language: 'javascript',
            code: `// Document Analysis with OpenAI client (AskArc Extension)
import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: 'fxk_your_api_key_here',
  baseURL: 'https://api.askarc.app/api/v1'
});

const completion = await client.chat.completions.create({
  model: 'gemini-1.5-pro',
  messages: [
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: 'Analyze this document'
        },
        {
          type: 'document',
          document: {
            data: 'JVBERi0xLjQKJdPr6eEKMSAwIG9iao...',
            mime_type: 'application/pdf',
            filename: 'analysis.pdf'
          }
        }
      ]
    }
  ],
  temperature: 0.7,
  max_tokens: 500
});

console.log(completion.choices[0].message.content);`
          },
          {
            language: 'python',
            code: `import requests

response = requests.post('https://api.askarc.app/api/v1/auth/login', 
    json={
        'email': 'user@fenxlabs.ai',
        'password': 'password123'
    }
)

data = response.json()
token = data['data']['token']`
          }
        ]
      }
    ],
    models: [
      {
        method: 'GET',
        path: '/api/v1/models',
        title: 'Get Available Models',
        description: 'Get list of all available AI models with their IDs and details',
        auth: 'api-key',
        responses: [
          {
            code: 200,
            description: 'Models retrieved successfully',
            example: [
              {
                id: 'model-identifier',
                name: 'Model Display Name',
                provider: 'openai',
                description: 'Brief model description',
                category: 'standard',
                contextWindow: 128000,
                maxTokens: 4096,
                supports_vision: true,
                supports_streaming: true,
                ranked_position: 1
              }
            ]
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/models \\
  -H "X-API-Key: fxk_your_api_key_here"`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/models', {
  headers: {
    'X-API-Key': 'fxk_your_api_key_here'
  }
});
const data = await response.json();
const models = data.data; // Array of available model objects`
          },
          {
            language: 'python',
            code: `import requests

response = requests.get('https://api.askarc.app/api/v1/models',
    headers={
        'X-API-Key': 'fxk_your_api_key_here'
    }
)
data = response.json()
models = data['data']  # List of available model objects`
          }
        ]
      }
    ],
    keys: [
      {
        method: 'POST',
        path: '/api/v1/portal/keys',
        title: 'Create API Key',
        description: 'Create a new API key for external usage',
        auth: 'jwt',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'name', type: 'string', required: true, description: 'Name for the API key', example: 'My App API Key' }
          ]
        },
        responses: [
          {
            code: 201,
            description: 'API key created successfully',
            example: {
              success: true,
              data: {
                id: 'key_id_123',
                name: 'My App API Key',
                key: 'fxk_1234567890abcdef...',
                isActive: true,
                usageCount: 0,
                creditsUsed: 0,
                createdAt: '2025-08-18T10:00:00Z'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/portal/keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "My App API Key"
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/portal/keys', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer ' + jwtToken,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'My App API Key'
  })
});

const data = await response.json();
const apiKey = data.data.key; // Save this securely!`
          }
        ]
      },
      {
        method: 'GET',
        path: '/api/v1/portal/keys',
        title: 'List API Keys',
        description: 'Get all API keys for the authenticated user',
        auth: 'jwt',
        responses: [
          {
            code: 200,
            description: 'API keys retrieved successfully',
            example: {
              success: true,
              data: [
                {
                  id: 'key_id_123',
                  name: 'My App API Key',
                  isActive: true,
                  usageCount: 150,
                  creditsUsed: 2.45,
                  lastUsedAt: '2025-08-18T09:30:00Z',
                  createdAt: '2025-08-15T10:00:00Z'
                }
              ]
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/portal/keys \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      },
      {
        method: 'DELETE',
        path: '/api/v1/portal/keys/:keyId',
        title: 'Revoke API Key',
        description: 'Permanently revoke an API key',
        auth: 'jwt',
        parameters: [
          { name: 'keyId', type: 'string', required: true, description: 'ID of the API key to revoke', example: 'key_id_123' }
        ],
        responses: [
          {
            code: 200,
            description: 'API key revoked successfully',
            example: {
              success: true,
              data: {
                message: 'API key revoked successfully'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X DELETE https://api.askarc.app/api/v1/portal/keys/key_id_123 \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      }
    ],
    credits: [
      {
        method: 'GET',
        path: '/api/v1/portal/credits',
        title: 'Get Credit Balance',
        description: 'Get current credit balance and usage information',
        auth: 'jwt',
        responses: [
          {
            code: 200,
            description: 'Credit balance retrieved successfully',
            example: {
              success: true,
              data: {
                balance: 15.75,
                totalUsed: 4.25,
                lastActivity: '2025-08-18T09:30:00Z'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X GET https://api.askarc.app/api/v1/portal/credits \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`
          }
        ]
      }
    ],
    openai_compatible: [
      {
        method: 'POST',
        path: '/api/v1/chat/completions',
        title: 'Chat Completions',
        description: 'OpenAI-compatible chat completions endpoint. Drop-in replacement for OpenAI API with the same request/response format.',
        auth: 'api-key',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'model', type: 'string', required: false, description: 'Model ID to use. Omit or pass "auto" to let the system pick the best model for the task.', example: 'gemini-2.0-flash' },
            { name: 'messages', type: 'array', required: true, description: 'Array of conversation messages with role and content', example: '[{"role": "user", "content": "Hello!"}]' },
            { name: 'temperature', type: 'number', required: false, description: 'Sampling temperature (0-2)', example: '0.7' },
            { name: 'max_tokens', type: 'number', required: false, description: 'Maximum tokens to generate', example: '150' },
            { name: 'top_p', type: 'number', required: false, description: 'Nucleus sampling parameter (0-1)', example: '1.0' },
            { name: 'stream', type: 'boolean', required: false, description: 'Enable SSE streaming', example: 'false' },
            { name: 'tools', type: 'array', required: false, description: 'Tool definitions. Pass {"type":"web_search_preview"} to enable live web search with citations.', example: '[{"type":"web_search_preview"}]' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'Chat completion generated successfully',
            example: {
              id: 'chatcmpl-123',
              object: 'chat.completion',
              created: 1677652288,
              model: 'gpt-4o-mini',
              choices: [{
                index: 0,
                message: {
                  role: 'assistant',
                  content: 'Hello! How can I help you today?'
                },
                finish_reason: 'stop'
              }],
              usage: {
                prompt_tokens: 9,
                completion_tokens: 12,
                total_tokens: 21
              }
            }
          },
          {
            code: 401,
            description: 'Invalid API key',
            example: {
              error: {
                message: 'Invalid or inactive API key',
                type: 'authentication_error',
                code: 'invalid_api_key'
              }
            }
          },
          {
            code: 403,
            description: 'Insufficient credits',
            example: {
              error: {
                message: 'Insufficient credits for this request',
                type: 'invalid_request_error',
                code: 'insufficient_credits'
              }
            }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {"role": "system", "content": "You are helpful."},
      {"role": "user", "content": "Hello!"}
    ],
    "temperature": 0.7,
    "max_tokens": 150
  }'`
          },
          {
            language: 'curl',
            code: `# Image Analysis Example
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": [
          {
            "type": "text",
            "text": "What do you see in this image?"
          },
          {
            "type": "image_url",
            "image_url": {
              "url": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
            }
          }
        ]
      }
    ],
    "temperature": 0.7,
    "max_tokens": 300
  }'`
          },
          {
            language: 'curl',
            code: `# Web Search Example
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gemini-2.0-flash",
    "messages": [
      {"role": "user", "content": "What AI companies launched products this week?"}
    ],
    "tools": [{"type": "web_search_preview"}]
  }'`
          },
          {
            language: 'curl',
            code: `# Document Analysis Example (AskArc Extension)
curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": [
          {
            "type": "text",
            "text": "Please analyze this document"
          },
          {
            "type": "document",
            "document": {
              "data": "JVBERi0xLjQKJdPr6eEKMSAwIG9iao...",
              "mime_type": "application/pdf",
              "filename": "report.pdf"
            }
          }
        ]
      }
    ],
    "temperature": 0.7,
    "max_tokens": 500
  }'`
          },
          {
            language: 'python',
            code: `# Using OpenAI Python library
import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

completion = client.chat.completions.create(
    messages=[
        {"role": "system", "content": "You are helpful."},
        {"role": "user", "content": "Hello!"}
    ],
    temperature=0.7,
    max_tokens=150
)

print(completion.choices[0].message.content)`
          },
          {
            language: 'python',
            code: `# Image Analysis with OpenAI Python library
import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

completion = client.chat.completions.create(
    messages=[
        {
            "role": "user",
            "content": [
                {
                    "type": "text",
                    "text": "What do you see in this image?"
                },
                {
                    "type": "image_url",
                    "image_url": {
                        "url": "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                    }
                }
            ]
        }
    ],
    temperature=0.7,
    max_tokens=300
)

print(completion.choices[0].message.content)`
          }
        ]
      },
      {
        method: 'POST',
        path: '/api/v1/chat/completions',
        title: 'Chat Completions (Streaming)',
        description: 'Stream chat completions in real-time using Server-Sent Events (SSE). Set "stream": true to receive incremental response chunks as they are generated.',
        auth: 'api-key',
        requestBody: {
          type: 'application/json',
          properties: [
            { name: 'messages', type: 'array', required: true, description: 'Array of conversation messages with role and content', example: '[{"role": "user", "content": "Hello!"}]' },
            { name: 'stream', type: 'boolean', required: true, description: 'Must be set to true to enable streaming', example: 'true' },
            { name: 'temperature', type: 'number', required: false, description: 'Sampling temperature (0-2)', example: '0.7' },
            { name: 'max_tokens', type: 'number', required: false, description: 'Maximum tokens to generate', example: '150' },
            { name: 'top_p', type: 'number', required: false, description: 'Nucleus sampling parameter (0-1)', example: '1.0' }
          ]
        },
        responses: [
          {
            code: 200,
            description: 'SSE stream (Content-Type: text/event-stream). Each line is "data: {chunk}" ending with "data: [DONE]"',
            example: {
              'Content-Type': 'text/event-stream',
              '_note': 'Each chunk arrives as: data: {json}',
              chunk: {
                id: 'chatcmpl-123',
                object: 'chat.completion.chunk',
                created: 1677652288,
                model: 'gpt-4o-mini',
                choices: [{ delta: { content: 'Hello' }, finish_reason: null }]
              },
              '_terminator': 'data: [DONE]'
            }
          },
          {
            code: 401,
            description: 'Invalid API key',
            example: { error: { message: 'Invalid or inactive API key', type: 'authentication_error', code: 'invalid_api_key' } }
          }
        ],
        examples: [
          {
            language: 'curl',
            code: `curl -X POST https://api.askarc.app/api/v1/chat/completions \\
  -H "X-API-Key: fxk_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  --no-buffer \\
  -d '{
    "messages": [
      {"role": "user", "content": "Tell me a short story."}
    ],
    "stream": true,
    "temperature": 0.7,
    "max_tokens": 300
  }'`
          },
          {
            language: 'javascript',
            code: `const response = await fetch('https://api.askarc.app/api/v1/chat/completions', {
  method: 'POST',
  headers: {
    'X-API-Key': 'fxk_your_api_key_here',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    messages: [{ role: 'user', content: 'Tell me a short story.' }],
    stream: true,
    temperature: 0.7,
    max_tokens: 300
  })
});

const reader = response.body.getReader();
const decoder = new TextDecoder();

while (true) {
  const { done, value } = await reader.read();
  if (done) break;

  const text = decoder.decode(value);
  for (const line of text.split('\\n')) {
    if (line.startsWith('data: ')) {
      const data = line.slice(6);
      if (data === '[DONE]') break;
      const chunk = JSON.parse(data);
      const content = chunk.choices[0]?.delta?.content || '';
      process.stdout.write(content);
    }
  }
}`
          },
          {
            language: 'python',
            code: `import openai

client = openai.OpenAI(
    api_key="fxk_your_api_key_here",
    base_url="https://api.askarc.app/api/v1"
)

stream = client.chat.completions.create(
    messages=[{"role": "user", "content": "Tell me a short story."}],
    stream=True,
    temperature=0.7,
    max_tokens=300
)

for chunk in stream:
    content = chunk.choices[0].delta.content or ""
    print(content, end="", flush=True)`
          }
        ]
      }
    ]
  };

  const availableModels = [
    { id: 'gpt-4o', name: 'GPT-4o', description: 'Most capable model, best for complex tasks' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini', description: 'Fast and cost-effective for most tasks' },
    { id: 'claude-3-5-sonnet', name: 'Claude 3.5 Sonnet', description: 'Excellent for analysis and writing' },
    { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Great for documents and large context' }
  ];

  const renderAuthBadge = (auth: string) => {
    const badges = {
      'jwt': { color: 'bg-blue-100 text-blue-800', text: 'JWT Required' },
      'api-key': { color: 'bg-green-100 text-green-800', text: 'API Key Required' },
      'none': { color: 'bg-gray-100 text-gray-800', text: 'No Auth' }
    };
    const badge = badges[auth as keyof typeof badges];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const renderMethod = (method: string) => {
    const colors = {
      'GET': 'text-green-600 bg-green-50',
      'POST': 'text-blue-600 bg-blue-50',
      'PUT': 'text-yellow-600 bg-yellow-50',
      'DELETE': 'text-red-600 bg-red-50'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${colors[method as keyof typeof colors]}`}>
        {method}
      </span>
    );
  };

  const SectionHeader: React.FC<{ 
    id: string; 
    title: string; 
    icon: React.ReactNode; 
    description?: string;
  }> = ({ id, title, icon, description }) => (
    <div 
      className="flex items-center justify-between cursor-pointer py-4 border-b border-gray-200"
      onClick={() => toggleSection(id)}
    >
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
        </div>
      </div>
      {openSections[id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">API Reference</h1>
        <p className="text-lg text-gray-600">
          Complete documentation for the AskArc API. Use these endpoints to integrate AI capabilities into your applications.
        </p>
      </div>

      {/* Quick Start */}
      <div className="mb-8">
        <SectionHeader
          id="getting_started"
          title="Quick Start"
          icon={<Book className="text-blue-600" size={24} />}
          description="Get up and running with the AskArc API in minutes"
        />
        
        {openSections.getting_started && (
          <div className="py-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Getting Started</h3>
              <div className="space-y-4 text-blue-800">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <div>
                    <p className="font-medium">Login to the Portal</p>
                    <p className="text-sm text-blue-700">Access the developer portal to manage your API keys and credits</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <div>
                    <p className="font-medium">Create an API Key</p>
                    <p className="text-sm text-blue-700">Generate a secure API key for your application</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <div>
                    <p className="font-medium">Start Making Requests</p>
                    <p className="text-sm text-blue-700">Use your API key to send messages and get AI responses</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Base URL</h3>
              <div className="bg-white border border-gray-200 rounded p-3 font-mono text-sm">
                https://api.askarc.app
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13.527.099C6.955-.744.942 3.9.099 10.473c-.843 6.572 3.8 12.584 10.373 13.428 6.573.843 12.587-3.801 13.428-10.374C24.744 6.955 20.101.943 13.527.099zM15.026 16.319c-.548 1.326-1.342 1.783-2.502 1.783-.904 0-1.932-.527-2.502-1.783-.546-1.2-.546-2.676 0-3.876.57-1.256 1.598-1.783 2.502-1.783 1.16 0 1.954.457 2.502 1.783.546 1.2.546 2.676 0 3.876zm-6.864-14.25c6.048-.84 11.62 3.42 12.46 9.468.84 6.048-3.42 11.62-9.468 12.46C5.106 24.837-.466 20.577-1.306 14.529c-.84-6.048 3.42-11.62 9.468-12.46z"/>
                </svg>
                Test with Postman
              </h3>
              <div className="space-y-4">
                <p className="text-orange-800">
                  We've created a complete Postman collection to help you test all API endpoints easily.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="https://www.postman.com/downloads/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Download Postman
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <button
                    onClick={() => {
                      const collectionUrl = '/portal/AskArc_API_Keys.postman_collection.json';
                      window.open(collectionUrl, '_blank');
                    }}
                    className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-orange-700 text-sm font-medium rounded-lg border border-orange-300 transition-colors"
                  >
                    Download Collection
                    <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </button>
                </div>
                <div className="bg-white border border-orange-200 rounded p-4">
                  <h4 className="font-medium text-orange-900 mb-2">Quick Setup:</h4>
                  <ol className="text-sm text-orange-800 space-y-1">
                    <li>1. Download and install Postman</li>
                    <li>2. Import our collection using the download link above</li>
                    <li>3. Set the <code className="bg-orange-100 px-1 rounded">baseUrl</code> variable to your API endpoint</li>
                    <li>4. Login via the Portal Authentication folder to get your JWT token</li>
                    <li>5. Create API keys and start testing!</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Authentication */}
      <div className="mb-8">
        <SectionHeader
          id="authentication"
          title="Authentication"
          icon={<Key className="text-green-600" size={24} />}
          description="Two authentication methods for different use cases"
        />
        
        {openSections.authentication && (
          <div className="py-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <Key size={20} />
                  API Key Authentication
                </h3>
                <p className="text-blue-800 mb-4">For external applications making API calls</p>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-blue-900">Header Format:</h4>
                    <code className="text-xs bg-blue-100 px-2 py-1 rounded block mt-1">X-API-Key: fxk_your_api_key_here</code>
                  </div>
                  <div>
                    <h4 className="font-medium text-blue-900">Alternative Format:</h4>
                    <code className="text-xs bg-blue-100 px-2 py-1 rounded block mt-1">Authorization: Bearer fxk_your_api_key_here</code>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <CheckCircle size={20} />
                  JWT Authentication
                </h3>
                <p className="text-green-800 mb-4">For portal management operations</p>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-green-900">Header Format:</h4>
                    <code className="text-xs bg-green-100 px-2 py-1 rounded block mt-1">Authorization: Bearer your_jwt_token</code>
                  </div>
                  <div>
                    <h4 className="font-medium text-green-900">Usage:</h4>
                    <p className="text-sm text-green-800">Required for API key management, credit operations, and analytics</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Authentication endpoints */}
            {apiEndpoints.authentication && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Portal Authentication</h3>
                {apiEndpoints.authentication.map((endpoint, index) => (
                  <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Models */}
      <div className="mb-8">
        <SectionHeader
          id="models"
          title="Available Models"
          icon={<Code className="text-purple-600" size={24} />}
          description="Get list of all available AI models"
        />
        
        {openSections.models && (
          <div className="py-6 space-y-6">
            {apiEndpoints.models?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* API Key Management */}
      <div className="mb-8">
        <SectionHeader
          id="api_keys"
          title="API Key Management"
          icon={<Key className="text-purple-600" size={24} />}
          description="Create, list, and manage your API keys"
        />
        
        {openSections.api_keys && (
          <div className="py-6 space-y-6">
            {apiEndpoints.keys?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Credit Management */}
      <div className="mb-8">
        <SectionHeader
          id="credits"
          title="Credit Management"
          icon={<CreditCard className="text-yellow-600" size={24} />}
          description="Monitor and manage your account credits"
        />
        
        {openSections.credits && (
          <div className="py-6 space-y-6">
            {apiEndpoints.credits?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Image Handling */}
      <div className="mb-8">
        <SectionHeader
          id="image_handling"
          title="Image Handling"
          icon={<Code className="text-purple-600" size={24} />}
          description="Send images using the standard OpenAI multimodal format"
        />
        
        {openSections.image_handling && (
          <div className="py-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Code size={20} />
                OpenAI Compatible Format
              </h3>
              <p className="text-blue-800 mb-4">Use multimodal content arrays in messages — the standard OpenAI format, fully supported by our endpoint.</p>
              <div className="bg-white border border-blue-200 rounded p-3">
                <pre className="text-xs text-blue-900 overflow-x-auto">
{`{
  "messages": [
    {
      "role": "user",
      "content": [
        {
          "type": "text",
          "text": "Analyze this image"
        },
        {
          "type": "image_url",
          "image_url": {
            "url": "data:image/png;base64,..."
          }
        }
      ]
    }
  ]
}`}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Chat Completions */}
      <div className="mb-8">
        <SectionHeader
          id="messages"
          title="Chat Completions (OpenAI Compatible)"
          icon={<Code className="text-indigo-600" size={24} />}
          description="OpenAI-compatible endpoint for chat completions — standard and streaming"
        />

        {openSections.messages && (
          <div className="py-6 space-y-6">
            {apiEndpoints.openai_compatible?.map((endpoint, index) => (
              <EndpointCard key={index} endpoint={endpoint} copyCode={copyCode} copiedCode={copiedCode} />
            ))}
          </div>
        )}
      </div>

      {/* Error Codes */}
      <div className="mb-8">
        <SectionHeader
          id="errors"
          title="Error Codes"
          icon={<AlertCircle className="text-red-600" size={24} />}
          description="Common error responses and how to handle them"
        />
        
        {openSections.errors && (
          <div className="py-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-4">Common Error Responses</h3>
              <div className="space-y-4">
                <ErrorCodeCard
                  code={401}
                  title="UNAUTHORIZED"
                  description="Invalid or missing authentication"
                  example={{
                    success: false,
                    error: {
                      code: 'UNAUTHORIZED',
                      message: 'Authentication required'
                    }
                  }}
                />
                <ErrorCodeCard
                  code={402}
                  title="INSUFFICIENT_CREDITS"
                  description="Not enough credits to complete the request"
                  example={{
                    success: false,
                    error: {
                      code: 'INSUFFICIENT_CREDITS',
                      message: 'Insufficient credits for this request',
                      details: {
                        required: 0.0025,
                        available: 0.001
                      }
                    }
                  }}
                />
                <ErrorCodeCard
                  code={404}
                  title="NOT_FOUND"
                  description="Resource not found"
                  example={{
                    success: false,
                    error: {
                      code: 'NOT_FOUND',
                      message: 'API key not found'
                    }
                  }}
                />
                <ErrorCodeCard
                  code={429}
                  title="RATE_LIMITED"
                  description="Too many requests"
                  example={{
                    success: false,
                    error: {
                      code: 'RATE_LIMITED',
                      message: 'Rate limit exceeded'
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Component for individual endpoint cards
const EndpointCard: React.FC<{
  endpoint: ApiEndpoint;
  copyCode: (code: string, id: string) => void;
  copiedCode: string | null;
}> = ({ endpoint, copyCode, copiedCode }) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {renderMethod(endpoint.method)}
            <code className="text-sm font-mono text-gray-900">{endpoint.path}</code>
            {renderAuthBadge(endpoint.auth)}
          </div>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mt-2">{endpoint.title}</h3>
        <p className="text-gray-600 mt-1">{endpoint.description}</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Request Body */}
        {endpoint.requestBody && (
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Request Body</h4>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="space-y-3">
                {endpoint.requestBody.properties.map((param, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <code className="text-sm bg-white px-2 py-1 rounded border">{param.name}</code>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">{param.type}</span>
                        {param.required && <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Required</span>}
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{param.description}</p>
                      {param.example && (
                        <code className="text-xs text-gray-600 mt-1 block">Example: {JSON.stringify(param.example)}</code>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Responses */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 mb-3">Responses</h4>
          <div className="space-y-3">
            {endpoint.responses.map((response, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-semibold ${response.code === 200 || response.code === 201 ? 'text-green-600' : 'text-red-600'}`}>
                      {response.code}
                    </span>
                    <span className="text-sm text-gray-700">{response.description}</span>
                  </div>
                </div>
                <div className="p-4">
                  <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                    <code>{JSON.stringify(response.example, null, 2)}</code>
                  </pre>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Code Examples */}
        {endpoint.examples && endpoint.examples.length > 0 && (
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-3">Code Examples</h4>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="flex">
                  {endpoint.examples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTab(index)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 ${
                        activeTab === index
                          ? 'border-blue-500 text-blue-600 bg-white'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {example.language}
                    </button>
                  ))}
                </div>
              </div>
              <div className="relative">
                <pre className="text-sm bg-gray-900 text-gray-100 p-4 overflow-x-auto">
                  <code>{endpoint.examples[activeTab]?.code}</code>
                </pre>
                <button
                  onClick={() => copyCode(endpoint.examples[activeTab]?.code || '', `${endpoint.method}-${endpoint.path}-${activeTab}`)}
                  className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded text-gray-300 hover:text-white"
                >
                  {copiedCode === `${endpoint.method}-${endpoint.path}-${activeTab}` ? (
                    <CheckCircle size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component for error codes
const ErrorCodeCard: React.FC<{
  code: number;
  title: string;
  description: string;
  example: any;
}> = ({ code, title, description, example }) => (
  <div className="bg-white border border-red-200 rounded-lg p-4">
    <div className="flex items-center gap-3 mb-2">
      <span className="text-red-600 font-semibold">{code}</span>
      <span className="font-medium text-red-900">{title}</span>
    </div>
    <p className="text-red-800 text-sm mb-3">{description}</p>
    <pre className="text-xs bg-red-50 p-3 rounded overflow-x-auto">
      <code>{JSON.stringify(example, null, 2)}</code>
    </pre>
  </div>
);

// Helper function to render method badges
const renderMethod = (method: string) => {
  const colors = {
    'GET': 'text-green-600 bg-green-50 border-green-200',
    'POST': 'text-blue-600 bg-blue-50 border-blue-200',
    'PUT': 'text-yellow-600 bg-yellow-50 border-yellow-200',
    'DELETE': 'text-red-600 bg-red-50 border-red-200'
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-semibold ${colors[method as keyof typeof colors]}`}>
      {method}
    </span>
  );
};

// Helper function to render auth badges
const renderAuthBadge = (auth: string) => {
  const badges = {
    'jwt': { color: 'bg-blue-100 text-blue-800 border-blue-200', text: 'JWT Required' },
    'api-key': { color: 'bg-green-100 text-green-800 border-green-200', text: 'API Key Required' },
    'none': { color: 'bg-gray-100 text-gray-800 border-gray-200', text: 'No Auth' }
  };
  const badge = badges[auth as keyof typeof badges];
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded border text-xs font-medium ${badge.color}`}>
      {badge.text}
    </span>
  );
};

export default APIReference;